import { useState, useCallback, useEffect, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { getAnimationTheme } from '@/types/animationThemes';
import { ClosedCard } from './ClosedCard';
import { renderTemplateLayout } from '@/components/templates/TemplateLayouts';
import { FullCanvasHandwritten } from './scenes/FullCanvasHandwritten';
import { FullCanvasFloral } from './scenes/FullCanvasFloral';
import { FullCanvasGoldenLight } from './scenes/FullCanvasGoldenLight';
import { FullCanvasBlooming } from './scenes/FullCanvasBlooming';
import { FullCanvasKeepsake } from './scenes/FullCanvasKeepsake';
import { FullCanvasConstellation } from './scenes/FullCanvasConstellation';
import { MessageActionsSection } from './MessageActionsSection';
import { CinematicShell } from './cinematic/CinematicShell';
import { buildAdaptiveConfig } from './cinematic/adaptive/adaptiveConfig';
import { resolveAudioBed } from './cinematic/adaptive/audioBeds';
import type { TemplateType } from '@/types/templates';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { RevealSemanticLayer } from './semantic/RevealSemanticLayer';
import { resolveMediaTransition } from './mediaTransitionPresets';

const EASE = [0.22, 0.61, 0.36, 1] as const;

const skipCopy = {
  en: { skip: 'Skip animation →' },
  es: { skip: 'Saltar animación →' },
  pt: { skip: 'Pular animação →' },
  fr: { skip: "Passer l'animation →" },
  ar: { skip: 'تخطي الحركة ←' },
  ja: { skip: 'アニメーションをスキップ →' },
  ko: { skip: '애니메이션 건너뛰기 →' },
  id: { skip: 'Lewati animasi →' },
};

interface PremiumAnnouncementRevealProps {
  animationTheme: string;
  recipientName: string;
  relationship: string;
  message: string;
  template: TemplateType | string;
  photoUrls?: string[];
  publishedDate?: string;
  skipReveal?: boolean;
  senderLabel?: string;
  metadata?: { chibiUrl?: string; [key: string]: any };
  children?: React.ReactNode;
  onRevealComplete?: () => void;
  /** Show "What do you want to do" section after reveal. Default true. */
  showActions?: boolean;
  /** Hide the replay button (e.g., for the original sender). */
  hideReplay?: boolean;
  /** Triggered when user taps "Share this moment" — opens existing share UI. */
  onShareRequest?: () => void;
  /** Stable ID used to seed the adaptive cinematic style. Same id = same experience on replay. */
  announcementId?: string | null;
}

/**
 * REVEAL ENGINE — Stable Reading State
 *
 * Flow:
 *   1. Optional ClosedCard gate
 *   2. Full-canvas reveal scene plays and ENDS in its own done state
 *      (the message stays beautifully rendered inside the scene)
 *   3. Scene REMAINS MOUNTED. We do NOT swap to the template layout —
 *      that swap previously felt like an unwanted "template selection" jump.
 *   4. Children + MessageActionsSection appear underneath, never auto-navigating.
 *
 * skipReveal mode (owner preview, no animation): we render the static template
 * layout directly — there is no scene to preserve.
 */
export function PremiumAnnouncementReveal({
  animationTheme,
  recipientName,
  relationship,
  message,
  template,
  photoUrls,
  publishedDate,
  skipReveal = false,
  senderLabel,
  metadata,
  children,
  onRevealComplete,
  showActions = true,
  hideReplay = false,
  onShareRequest,
  announcementId,
}: PremiumAnnouncementRevealProps) {
  const theme = getAnimationTheme(animationTheme);
  const { language } = useLanguage();
  const t = lc(skipCopy, language);

  // Adaptive cinematic config — stable per-announcement variation.
  const adaptiveConfig = useMemo(
    () => buildAdaptiveConfig({
      seedKey: announcementId,
      animationTheme: theme.value,
      template: typeof template === 'string' ? template : undefined,
      recipientName,
      message,
      relationship,
      photoUrls,
      publishedDate,
    }),
    [announcementId, theme.value, template, recipientName, message, relationship, photoUrls, publishedDate],
  );

  const [showClosed, setShowClosed] = useState(!skipReveal);
  const [revealPhase, setRevealPhase] = useState<'closed' | 'revealing' | 'done'>(
    skipReveal ? 'done' : 'closed'
  );
  // Scene replay key — incrementing remounts the scene for "Feel this again"
  const [sceneKey, setSceneKey] = useState(0);
  // Media/action gate — opens immediately when the final text chunk completes.
  const [actionsVisible, setActionsVisible] = useState(skipReveal);

  useEffect(() => {
    if (revealPhase !== 'done') {
      setActionsVisible(false);
      return;
    }
    if (skipReveal) {
      setActionsVisible(true);
      return;
    }
    setActionsVisible(true);
  }, [revealPhase, skipReveal, sceneKey]);

  const handleOpen = useCallback(() => {
    setShowClosed(false);
    setRevealPhase('revealing');
  }, []);

  const handleSkip = useCallback(() => {
    // Skip moves the SCENE to its done state via remount-with-skip semantics:
    // we keep the scene rendered (so the message stays visible) but mark
    // reveal complete so children/actions appear.
    setRevealPhase('done');
    onRevealComplete?.();
  }, [onRevealComplete]);

  const handleSceneComplete = useCallback(() => {
    setRevealPhase('done');
    onRevealComplete?.();
  }, [onRevealComplete]);

  const handleReplay = useCallback(() => {
    // Replay: rewind to revealing phase and remount the scene
    setRevealPhase('revealing');
    setSceneKey((k) => k + 1);
    // Smooth scroll to top so the user catches the start of the animation
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    if (skipReveal) {
      onRevealComplete?.();
    }
  }, [skipReveal, onRevealComplete]);

  const sceneProps = {
    recipientName,
    relationship,
    message,
    photoUrls,
    publishedDate,
    senderLabel,
    onComplete: handleSceneComplete,
    config: adaptiveConfig,
    startAtMessage: true,
  };

  // Invisible semantic/transcript layer — never paints pixels, but gives AI
  // crawlers and screen readers the full emotional context + transcript.
  const semantic = (
    <RevealSemanticLayer
      recipientName={recipientName}
      relationship={relationship}
      senderLabel={senderLabel}
      message={message}
      template={typeof template === 'string' ? template : undefined}
      animationTheme={animationTheme}
      publishedDate={publishedDate}
      photoUrls={photoUrls}
      announcementId={announcementId}
    />
  );

  // Phase 1: Closed card — premium intro screen
  if (showClosed && revealPhase === 'closed') {
    return (
      <>
        {semantic}
        <AnimatePresence mode="wait">

        <motion.div
          key="closed"
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.6, ease: EASE as any }}
        >
          <ClosedCard
            theme={theme}
            recipientName={recipientName}
            senderLabel={senderLabel}
            onOpen={handleOpen}
          />
        </motion.div>
        </AnimatePresence>
      </>
    );
  }

  // Skip-reveal owner preview: no scene exists, render static template
  if (skipReveal) {
    return (
      <>
        {semantic}
        <motion.div
          initial={false}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, ease: EASE as any }}
        >
          {renderTemplateLayout(template, {
            recipientName,
            relationship,
            message,
            photoUrls,
            publishedDate,
            senderLabel,
            metadata,
          })}
          {children}
        </motion.div>
      </>
    );
  }


  // Phases 2 + 3: Scene stays mounted. Children & actions appear after reveal.
  // The scene itself renders the message in its `done` phase — we never swap
  // to a different template layout.
  const RevealScene = getRevealScene(theme.value);

  return (
    <>
      {semantic}
    <CinematicShell
      themeValue={theme.value}
      config={adaptiveConfig}
      audioSrc={resolveAudioBed(adaptiveConfig.audioBedKey)}
      photoUrls={photoUrls}
      // After the first play we skip the cinematic intro on replay
      skipIntro={sceneKey > 0}
      sceneDone={revealPhase === 'done'}
      onIntroComplete={() => { /* scene mounts now via render-prop */ }}
    >
      {() => (
        <div className="relative">
          <RevealScene key={sceneKey} {...sceneProps} />

          {/* Skip button — only visible while still revealing */}
          {revealPhase === 'revealing' && (
            <motion.div
              className="fixed bottom-8 left-0 right-0 flex justify-center z-50 pointer-events-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 6.0 }}
            >
              <button
                onClick={handleSkip}
                className="pointer-events-auto text-xs text-white/75 hover:text-white transition-colors px-4 py-2 backdrop-blur-sm bg-black/30 rounded-full"
              >
                {t.skip}
              </button>
            </motion.div>
          )}

          {/* After reveal: children (Listen button, video, etc.) and action section.
              Gated on actionsVisible so the moment is allowed to breathe first. */}
          {revealPhase === 'done' && actionsVisible && (() => {
            const mediaTransition = resolveMediaTransition(
              (metadata as any)?.mediaTransition,
              theme.value,
            );
            return (
              <motion.div
                initial={mediaTransition.initial as any}
                animate={mediaTransition.animate as any}
                transition={mediaTransition.transition}
                className="relative z-30"
              >
                {children}
                {showActions && (
                  <MessageActionsSection
                    prefill={{
                      relationship,
                      template: typeof template === 'string' ? template : undefined,
                      animationTheme,
                    }}
                    onReplay={handleReplay}
                    onShare={onShareRequest}
                    hideReplay={hideReplay}
                  />
                )}
              </motion.div>
            );
          })()}
        </div>
      )}
    </CinematicShell>
    </>
  );
}


function getRevealScene(themeValue: string) {
  switch (themeValue) {
    case 'handwritten_journal': return FullCanvasHandwritten;
    case 'elegant_floral': return FullCanvasFloral;
    case 'golden_light': return FullCanvasGoldenLight;
    case 'blooming_garden': return FullCanvasBlooming;
    case 'ornate_keepsake': return FullCanvasKeepsake;
    case 'memory_constellation': return FullCanvasConstellation;
    default: return FullCanvasFloral;
  }
}
